import { apiService } from './api';

export interface PersonalizationTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  fontFamily: string;
  borderRadius: string;
  shadows: boolean;
}

export interface BrandingSettings {
  logo?: string;
  companyName?: string;
  favicon?: string;
  brandColors: string[];
  showLogo?: boolean;
  typography: {
    headingFont: string;
    bodyFont: string;
  };
}

export interface PersonalizationSettings {
  theme: PersonalizationTheme;
  branding: BrandingSettings;
  customization: {
    menuStyle: 'sidebar' | 'topbar' | 'compact';
    cardStyle: 'elevated' | 'outlined' | 'filled';
    animations: boolean;
    density: 'comfortable' | 'compact' | 'spacious';
  };
}

export interface WebsiteAnalysis {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
  typography: {
    headingFont: string;
    bodyFont: string;
  };
  branding: {
    logo?: string;
    companyName: string;
    industry: string;
    style: 'modern' | 'classic' | 'minimal' | 'bold';
  };
  layout: {
    style: 'clean' | 'corporate' | 'creative' | 'technical';
    spacing: 'tight' | 'normal' | 'loose';
  };
}

class PersonalizationService {
  private currentSettings: PersonalizationSettings | null = null;
  private lastAppliedTheme: string | null = null;
  private isThemeApplied: boolean = false;
  // 🧠 Layer 9 - Promise singleton to prevent duplicate loads
  private loadPromise: Promise<PersonalizationSettings> | null = null;
  // 🔄 Workspace-specific cache
  private currentWorkspaceId: string | null = null;
  // 🛡️ Prevent backend from overwriting recently saved theme
  private lastSaveTimestamp: number = 0;
  private static SAVE_PROTECTION_MS = 10000; // 10 seconds protection after save

  async analyzeCompanyWebsite(url: string): Promise<WebsiteAnalysis> {
    try {
      console.log('🎨 Personalization: Analyzing website...', url);
      const analysis = await apiService.analyzeWebsite(url);
      console.log('🎨 Personalization: Website analysis complete:', analysis);
      return analysis;
    } catch (error) {
      console.error('🎨 Personalization: Website analysis failed:', error);
      throw error;
    }
  }

  async generatePersonalizedTheme(websiteAnalysis: WebsiteAnalysis): Promise<PersonalizationSettings> {
    try {
      console.log('🎨 Personalization: Generating personalized theme...');
      const theme = await apiService.generatePersonalizedTheme(websiteAnalysis);
      console.log('🎨 Personalization: Theme generated:', theme);
      return theme;
    } catch (error) {
      console.error('🎨 Personalization: Theme generation failed:', error);
      throw error;
    }
  }

  // 🔄 Set current workspace and invalidate cache if changed
  setCurrentWorkspace(workspaceId: string): void {
    if (this.currentWorkspaceId !== workspaceId) {
      console.log(`🔄 Personalization: Workspace changed from ${this.currentWorkspaceId} to ${workspaceId}`);
      this.currentWorkspaceId = workspaceId;
      // Clear in-memory cache
      this.currentSettings = null;
      this.lastAppliedTheme = null;
      this.loadPromise = null;
      // Don't clear localStorage - we use workspace-specific keys now
    }
  }

  // 🔄 Clear all personalization cache
  clearCache(): void {
    console.log('🗑️ Personalization: Clearing all cache');
    this.currentSettings = null;
    this.lastAppliedTheme = null;
    this.loadPromise = null;
    // Clear all personalization-related localStorage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('personalization-settings')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`🗑️ Cleared ${keysToRemove.length} personalization cache entries`);
  }

  // 🧠 Layer 9 - Cache-first approach for instant load (workspace-specific)
  private getCachedSettings(): PersonalizationSettings | null {
    try {
      // Use workspace-specific cache key
      const cacheKey = this.currentWorkspaceId 
        ? `personalization-settings-${this.currentWorkspaceId}`
        : 'personalization-settings';
      
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const settings = JSON.parse(cached);
        console.log(`⚡ Personalization: Using cached settings for workspace ${this.currentWorkspaceId || 'default'} (instant load)`);
        return settings;
      }
      
      // Also check old cache key for migration (one-time)
      if (this.currentWorkspaceId) {
        const oldCache = localStorage.getItem('personalization-settings');
        if (oldCache) {
          console.log('⚠️ Personalization: Found old cache, migrating to workspace-specific key');
          // Don't use old cache for new workspaces - force fresh load
          localStorage.removeItem('personalization-settings');
        }
      }
    } catch (error) {
      console.warn('🎨 Personalization: Failed to read cache:', error);
    }
    return null;
  }

  // 🧠 Cache settings with workspace-specific key + last-used for FOUC prevention
  private setCachedSettings(settings: PersonalizationSettings): void {
    try {
      const cacheKey = this.currentWorkspaceId 
        ? `personalization-settings-${this.currentWorkspaceId}`
        : 'personalization-settings';
      localStorage.setItem(cacheKey, JSON.stringify(settings));
      
      // 🎨 FOUC Prevention: Always update last-used cache for boot script
      localStorage.setItem('personalization-last-used', JSON.stringify(settings));
      console.log(`🎨 Personalization: Cached settings for workspace ${this.currentWorkspaceId || 'default'} + last-used`);
    } catch (error) {
      console.warn('🎨 Personalization: Failed to cache settings:', error);
    }
  }

  async getPersonalizationSettings(): Promise<PersonalizationSettings> {
    // 🧠 Layer 9 - Step 1: Try cache first (instant!) - only if we have workspace set
    if (this.currentWorkspaceId) {
      const cached = this.getCachedSettings();
      if (cached) {
        this.currentSettings = cached;
        // Apply cached theme (already set by boot script, but ensure consistency)
        this.applyThemeToDOM(cached.theme, cached.branding, false);
        return cached;
      }
    }

    // 🧠 Layer 9 - Step 2: Check if already loading (Promise singleton)
    if (this.loadPromise) {
      console.log('⏳ Personalization: Already loading, reusing Promise...');
      return this.loadPromise;
    }

    // 🧠 Layer 9 - Step 3: Create new load Promise
    console.log('🎨 Personalization: Loading settings from backend...');
    this.loadPromise = (async () => {
      try {
        const settings = await apiService.getPersonalizationSettings();
        this.currentSettings = settings;
        console.log('🎨 Personalization: Settings loaded successfully:', settings);
        
        // 🛡️ Check if we recently saved - if so, skip forceApply to prevent overwriting
        const timeSinceSave = Date.now() - this.lastSaveTimestamp;
        const isProtected = timeSinceSave < PersonalizationService.SAVE_PROTECTION_MS;
        
        if (isProtected) {
          console.log(`🛡️ Save protection active (${Math.round(timeSinceSave / 1000)}s ago) - skipping backend force-apply`);
          // Don't force-apply, just use cached settings
          this.applyThemeToDOM(settings.theme, settings.branding, false);
        } else {
          // 🧠 Layer 7 - Force apply backend theme (overrides boot script defaults)
          this.applyThemeToDOM(settings.theme, settings.branding, true);
          console.log('✅ Backend theme forcefully applied (overrides cache/boot)');
        }
        
        // Cache settings to localStorage for faster next load (workspace-specific)
        this.setCachedSettings(settings);
        
        return settings;
      } catch (error) {
        console.error('🎨 Personalization: Failed to load settings from backend:', error);
        
        // If we have cached settings, use them as fallback
        if (this.currentSettings) {
          console.log('🎨 Personalization: Using cached settings as fallback:', this.currentSettings);
          return this.currentSettings;
        }
        
        // Return default settings if no cache and loading fails
        const defaultSettings = this.getDefaultSettings();
        console.log('🎨 Personalization: Using default settings due to API failure:', defaultSettings);
        return defaultSettings;
      } finally {
        // Clear Promise after completion
        this.loadPromise = null;
      }
    })();

    return this.loadPromise;
  }

  async updatePersonalizationSettings(settings: Partial<PersonalizationSettings>): Promise<PersonalizationSettings> {
    try {
      console.log('🎨 Personalization: Updating settings...', settings);
      const updatedSettings = await apiService.updatePersonalizationSettings(settings);
      this.currentSettings = updatedSettings;
      
      // 🛡️ Set timestamp to protect against backend re-fetch overwriting
      this.lastSaveTimestamp = Date.now();
      console.log(`🛡️ Save protection enabled for ${PersonalizationService.SAVE_PROTECTION_MS / 1000}s`);
      
      // Cache updated settings (workspace-specific)
      this.setCachedSettings(updatedSettings);
      
      // 🧠 Layer 7 - Force apply updated theme
      this.applyThemeToDOM(updatedSettings.theme, updatedSettings.branding, true);
      
      // Dispatch event to notify App component of theme change
      const event = new CustomEvent('personalization-updated', {
        detail: updatedSettings
      });
      window.dispatchEvent(event);
      
      console.log('🎨 Personalization: Settings updated:', updatedSettings);
      return updatedSettings;
    } catch (error) {
      console.error('🎨 Personalization: Failed to update settings:', error);
      throw error;
    }
  }

  // 🧠 Layer 7 - Color Stability Fix
  applyThemeToDOM(theme: PersonalizationTheme, branding?: BrandingSettings, forceApply: boolean = false): void {
    // Compare with last applied theme to prevent unnecessary re-renders (flash)
    const themeString = JSON.stringify({ theme, branding });
    
    // Skip comparison if forceApply is true (backend theme always wins)
    if (!forceApply && this.lastAppliedTheme === themeString) {
      console.log('🎨 Theme unchanged - skipping re-apply (prevents flash)');
      return;
    }
    
    const logPrefix = forceApply ? '🎨 [FORCE]' : '🎨';
    console.log(`${logPrefix} Personalization: Applying theme to DOM...`, theme, branding);
    
    const root = document.documentElement;
    
    // Apply CSS custom properties for complete personalization
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--secondary-color', theme.secondaryColor);
    root.style.setProperty('--accent-color', theme.accentColor);
    root.style.setProperty('--background-color', theme.backgroundColor);
    root.style.setProperty('--surface-color', theme.surfaceColor);
    root.style.setProperty('--text-color', theme.textColor);
    root.style.setProperty('--font-family', theme.fontFamily);
    root.style.setProperty('--border-radius', theme.borderRadius);
    
    // Apply typography from branding settings
    if (branding?.typography) {
      console.log('🎨 Loading fonts:', {
        heading: branding.typography.headingFont,
        body: branding.typography.bodyFont
      });
      
      // Load Google Fonts if needed
      this.loadGoogleFont(branding.typography.headingFont);
      this.loadGoogleFont(branding.typography.bodyFont);
      
      root.style.setProperty('--heading-font', branding.typography.headingFont);
      root.style.setProperty('--body-font', branding.typography.bodyFont);
    }
    
    // Apply shadows
    if (theme.shadows) {
      root.style.setProperty('--box-shadow', '0 2px 8px rgba(0,0,0,0.1)');
      root.style.setProperty('--card-shadow', '0 4px 12px rgba(0,0,0,0.15)');
    } else {
      root.style.setProperty('--box-shadow', 'none');
      root.style.setProperty('--card-shadow', 'none');
    }

    // Apply customization settings
    if (branding) {
      root.style.setProperty('--menu-style', branding.showLogo ? 'with-logo' : 'no-logo');
    }

    // Save as last applied to prevent re-applying same theme
    this.lastAppliedTheme = themeString;
    this.isThemeApplied = true;
    
    // Enable smooth transitions now that theme is ready
    document.documentElement.classList.add('theme-ready');
    document.body.classList.add('theme-ready');
    
    console.log('✅ Theme applied to DOM (transitions enabled, flag set)');
  }

  /**
   * Check if theme has been applied
   */
  public hasThemeApplied(): boolean {
    return this.isThemeApplied;
  }

  async initializePersonalization(): Promise<void> {
    try {
      console.log('🎨 Personalization: Initializing...');
      const settings = await this.getPersonalizationSettings();
      this.applyThemeToDOM(settings.theme, settings.branding);
      console.log('🎨 Personalization: Initialization complete');
    } catch (error) {
      console.error('🎨 Personalization: Initialization failed:', error);
      // Apply default theme on failure
      const defaultSettings = this.getDefaultSettings();
      this.applyThemeToDOM(defaultSettings.theme, defaultSettings.branding);
    }
  }

  private getDefaultSettings(): PersonalizationSettings {
    return {
      theme: {
        primaryColor: '#e67e22',
        secondaryColor: '#27ae60',
        accentColor: '#ff5722',
        backgroundColor: '#f8f9fa',
        surfaceColor: '#ffffff',
        textColor: '#212121',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        borderRadius: '8px',
        shadows: true,
      },
      branding: {
        companyName: 'Time Tracker',
        brandColors: ['#1976d2', '#dc004e'],
        typography: {
          headingFont: 'Roboto',
          bodyFont: 'Roboto',
        },
      },
      customization: {
        menuStyle: 'sidebar',
        cardStyle: 'elevated',
        animations: true,
        density: 'comfortable',
      },
    };
  }

  // Helper methods for specific customizations
  async updateThemeColors(colors: Partial<PersonalizationTheme>): Promise<void> {
    const currentSettings = await this.getPersonalizationSettings();
    const updatedTheme = { ...currentSettings.theme, ...colors };
    await this.updatePersonalizationSettings({ theme: updatedTheme });
  }

  async updateBranding(branding: Partial<BrandingSettings>): Promise<void> {
    const currentSettings = await this.getPersonalizationSettings();
    const updatedBranding = { ...currentSettings.branding, ...branding };
    await this.updatePersonalizationSettings({ branding: updatedBranding });
  }

  async analyzeLogo(logoUrl: string): Promise<any> {
    try {
      // For development/testing, use mock data if API fails
      try {
        const analysis = await apiService.analyzeLogo(logoUrl);
        console.log('🎨 Logo analysis complete:', analysis);
        return analysis;
      } catch (apiError) {
        console.warn('🎨 API analysis failed, using mock data:', apiError);
        
        // Generate mock analysis with dramatic colors for testing
        let mockAnalysis = {
          dominantColors: ['#E91E63', '#9C27B0', '#673AB7'], // Vibrant pink/purple theme
          style: 'geometric' as 'geometric' | 'organic' | 'text-based' | 'icon' | 'combination',
          complexity: 'moderate' as 'simple' | 'moderate' | 'complex',
          orientation: 'horizontal' as 'horizontal' | 'vertical' | 'square',
          elements: ['geometric', 'modern', 'clean'],
          modernityScore: 9
        };
        
        // If it's Amazon logo, use Amazon's actual brand colors
        if (logoUrl.includes('amazon') || logoUrl.includes('Amazon')) {
          mockAnalysis = {
            dominantColors: ['#FF9900', '#232F3E', '#37475A'], // Amazon orange, dark blue, gray
            style: 'combination' as 'geometric' | 'organic' | 'text-based' | 'icon' | 'combination',
            complexity: 'simple' as 'simple' | 'moderate' | 'complex',
            orientation: 'horizontal' as 'horizontal' | 'vertical' | 'square',
            elements: ['text', 'arrow', 'smile'],
            modernityScore: 8
          };
        }
        
        // Add more variety based on different logo types
        if (logoUrl.includes('google') || logoUrl.includes('Google')) {
          mockAnalysis = {
            dominantColors: ['#4285F4', '#EA4335', '#FBBC05'], // Google colors
            style: 'text-based' as 'geometric' | 'organic' | 'text-based' | 'icon' | 'combination',
            complexity: 'simple' as 'simple' | 'moderate' | 'complex',
            orientation: 'horizontal' as 'horizontal' | 'vertical' | 'square',
            elements: ['text', 'colorful', 'friendly'],
            modernityScore: 9
          };
        }
        
        console.log('🎨 Using mock logo analysis:', mockAnalysis);
        return mockAnalysis;
      }
    } catch (error) {
      console.error('🎨 Logo analysis completely failed:', error);
      throw error;
    }
  }

  async resetToDefaults(): Promise<void> {
    console.log('🎨 Personalization: Resetting to defaults...');
    const defaultSettings = this.getDefaultSettings();
    await this.updatePersonalizationSettings(defaultSettings);
  }

  async generateBrandIcon(params: {
    companyName: string;
    industry?: string;
    style?: string;
    primaryColor?: string;
    isLightNavigation?: boolean;
  }): Promise<any> {
    try {
      console.log('🎨 Personalization: Generating brand icon...', params);
      const result = await apiService.generateBrandIcon(params);
      console.log('🎨 Personalization: Brand icon generated:', result);
      return result;
    } catch (error) {
      console.error('🎨 Personalization: Brand icon generation failed:', error);
      throw error;
    }
  }

  private loadGoogleFont(fontName: string): void {
    // Skip if it's a standard font or already loaded
    if (!fontName || fontName.includes('Arial') || fontName.includes('sans-serif') || fontName.includes('Roboto')) {
      return;
    }

    // Extract the font family name (remove fallbacks)
    const cleanFontName = fontName.split(',')[0].trim().replace(/['"]/g, '');
    
    // Skip system fonts entirely (no need to load from web)
    const systemFonts = ['Helvetica Neue', 'Helvetica', 'Arial', 'San Francisco', 'Segoe UI', 'Roboto'];
    if (systemFonts.some(sf => cleanFontName.includes(sf))) {
      console.log(`✅ Using system font (no web load needed): ${cleanFontName}`);
      this.setSystemFontFallback(cleanFontName);
      return;
    }
    
    // Check if font is already loaded
    const existingLink = document.querySelector(`link[href*="${cleanFontName.replace(/\s+/g, '+')}"]`);
    if (existingLink) {
      console.log(`✅ Font already loaded: ${cleanFontName}`);
      return;
    }

    // Try multiple font sources
    this.tryLoadFont(cleanFontName);
  }

  private async tryLoadFont(fontName: string): Promise<void> {
    console.log(`🎨 Attempting to load font: ${fontName}`);

    // 1. First try Google Fonts (most common)
    const googleFontsUrl = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
    
    try {
      await this.loadFontFromUrl(googleFontsUrl, 'Google Fonts');
      console.log(`✅ Successfully loaded ${fontName} from Google Fonts`);
      return;
    } catch (error) {
      console.log(`❌ ${fontName} not available on Google Fonts, trying alternatives...`);
    }

    // 2. Try Adobe Fonts (common for enterprise)
    const adobeFontsUrl = `https://use.typekit.net/css?family=${fontName.replace(/\s+/g, '-').toLowerCase()}`;
    try {
      await this.loadFontFromUrl(adobeFontsUrl, 'Adobe Fonts');
      console.log(`✅ Successfully loaded ${fontName} from Adobe Fonts`);
      return;
    } catch (error) {
      console.log(`❌ ${fontName} not available on Adobe Fonts`);
    }

    // 3. Try system font stack as fallback
    console.log(`⚠️ Font ${fontName} not available from web sources, using system fallback`);
    this.setSystemFontFallback(fontName);
  }

  private loadFontFromUrl(url: string, source: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      
      link.onload = () => {
        console.log(`🎨 Font loaded successfully from ${source}: ${url}`);
        resolve();
      };
      
      link.onerror = () => {
        console.warn(`🎨 Failed to load font from ${source}: ${url}`);
        document.head.removeChild(link); // Clean up failed link
        reject(new Error(`Failed to load from ${source}`));
      };
      
      document.head.appendChild(link);
    });
  }

  private setSystemFontFallback(originalFont: string): void {
    // Create intelligent fallbacks based on font characteristics
    let fallbackStack = '';
    
    const fontLower = originalFont.toLowerCase();
    
    // Serif fonts
    if (fontLower.includes('serif') || fontLower.includes('times') || fontLower.includes('georgia')) {
      fallbackStack = `"${originalFont}", Georgia, "Times New Roman", Times, serif`;
    }
    // Sans-serif fonts (most common for brands)
    else if (fontLower.includes('sans') || fontLower.includes('arial') || fontLower.includes('helvetica')) {
      fallbackStack = `"${originalFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
    }
    // Monospace fonts
    else if (fontLower.includes('mono') || fontLower.includes('code') || fontLower.includes('courier')) {
      fallbackStack = `"${originalFont}", "SF Mono", Monaco, Inconsolata, "Roboto Mono", Consolas, "Courier New", monospace`;
    }
    // Brand-specific intelligent fallbacks
    else {
      // For brand fonts, create smart fallbacks based on common characteristics
      if (fontLower.includes('google') || fontLower.includes('product')) {
        fallbackStack = `"${originalFont}", "Google Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      } else if (fontLower.includes('apple') || fontLower.includes('sf')) {
        fallbackStack = `"${originalFont}", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif`;
      } else if (fontLower.includes('microsoft') || fontLower.includes('segoe')) {
        fallbackStack = `"${originalFont}", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, sans-serif`;
      } else if (fontLower.includes('amazon') || fontLower.includes('ember')) {
        fallbackStack = `"${originalFont}", "Amazon Ember", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      } else {
        // Generic high-quality fallback for unknown brand fonts
        fallbackStack = `"${originalFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
      }
    }

    // Update CSS variable with fallback stack
    const root = document.documentElement;
    root.style.setProperty('--heading-font', fallbackStack);
    
    console.log(`🎨 Set system font fallback: ${fallbackStack}`);
  }

  // Utility methods
  generateColorPalette(baseColor: string): string[] {
    // Simple color palette generation (in a real app, you'd use a more sophisticated algorithm)
    const colors = [baseColor];
    
    // Generate lighter and darker variants
    const hsl = this.hexToHsl(baseColor);
    if (hsl) {
      colors.push(this.hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 0.2, 1)));
      colors.push(this.hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 0.2, 0)));
      colors.push(this.hslToHex((hsl.h + 30) % 360, hsl.s, hsl.l));
      colors.push(this.hslToHex((hsl.h - 30 + 360) % 360, hsl.s, hsl.l));
    }
    
    return colors;
  }

  private hexToHsl(hex: string): { h: number; s: number; l: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;

    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return { h: h * 360, s, l };
  }

  private hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0;
    let g = 0;
    let b = 0;

    if (0 <= h && h < 60) {
      r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
      r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
      r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
      r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
      r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
      r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}

export const personalizationService = new PersonalizationService();
